// Surface palette + physics properties.
//
// Two parallel maps share the same keys:
//   SURFACE_PROPS  — physics + label colors used by the simulator HUD
//   SURFACE_COLORS — translucent fills + strokes used by the editor's
//                    patch overlay rendering
// Co-locating them here keeps the keys atomic — renaming a surface
// can't drift between sim and editor.
//
// CURB_COLORS uses the flat shape (`red: '#e54b3c'`). The editor's
// monolithic HTML still has the older nested shape; that gets unified
// when editor/src/ extraction touches the patch-panel rendering.

export const SURFACE_PROPS = {
  tarmac: { gripMul: 1.00, drag:   0, color: '#272b28', label: 'TARMAC', textColor: '#6cdf6c' },
  curb:   { gripMul: 0.92, drag:   0, color: '#e54b3c', label: 'CURB',   textColor: '#ffae3b' },
  grass:  { gripMul: 0.35, drag:  35, color: '#3d7a3a', label: 'GRASS',  textColor: '#ffae3b' },
  sand:   { gripMul: 0.22, drag: 180, color: '#d8bc82', label: 'SAND',   textColor: '#ffae3b' },
  gravel: { gripMul: 0.10, drag: 350, color: '#b08555', label: 'GRAVEL', textColor: '#e54b3c' },
  wall:   { gripMul: 0.00, drag:   0, color: '#9a9da3', label: 'WALL',   textColor: '#e54b3c' },
};

// Translucent fills/strokes for editor patch rendering. Stroke values are
// slightly more opaque for clear boundary lines.
export const SURFACE_COLORS = {
  grass:  { fill: 'rgba(74, 124, 74, 0.40)',   stroke: 'rgba(110, 170, 110, 0.85)' },
  gravel: { fill: 'rgba(212, 184, 138, 0.45)', stroke: 'rgba(212, 184, 138, 0.95)' },
  sand:   { fill: 'rgba(230, 209, 150, 0.45)', stroke: 'rgba(230, 209, 150, 0.95)' },
  tarmac: { fill: 'rgba(74, 77, 82, 0.55)',    stroke: 'rgba(160, 165, 172, 0.85)' },
  curb:   { fill: 'rgba(229, 75, 60, 0.35)',   stroke: 'rgba(255, 255, 255, 0.85)' },
};

// Editor's patch-mode picker only offers paintable surfaces (no `wall` —
// walls are authored in walls mode, not as patches).
export const SURFACE_TYPES = ['grass', 'gravel', 'sand', 'tarmac', 'curb'];

// Curb stripe colors. Red curbs alternate red/white chunks (Monaco /
// classic F1); other colors render as a solid band — drivers asked for
// solid yellow/blue/grey accents without the candy-stripe.
export const CURB_COLORS = {
  red:    '#e54b3c',
  yellow: '#f5c441',
  blue:   '#3a7ad6',
  grey:   '#8c93a0',
};

// Tarmac surface-patch tints. Default tracks the SURFACE_PROPS.tarmac
// color; the others let authors mark visually distinct paved zones
// (apron, alt line) without changing physics.
export const TARMAC_COLORS = {
  default: '#272b28',
  light:   '#3a3f3c',
  dark:    '#1a1d1b',
  blue:    '#1f2a3a',
  red:     '#3a1f1f',
};

// Defaults unknown surface names to grass — protects against typos in
// authored data and forward-compat with future surface types.
export function validSurface(name) {
  return SURFACE_PROPS[name] ? name : 'grass';
}

// Z-order for surface patches when multiple types overlap. Lower
// number = drawn first = visually under. Renderers and the simulator's
// surfaceAt collision lookup share this order so the rendered visual
// matches what the car drives on.
//
//   grass   — runs under everything (default ground)
//   gravel  — coarse runoff
//   sand    — sand traps
//   tarmac  — paved extensions of the racing surface
//   curb    — painted on top of tarmac patches at corner apexes; the
//             main racing strip still paints over curbs in the overlap
//             (see drawTrack layer order in simulator/src/render.js).
//
// Walls aren't patches, but they render on top of all patches (and
// on top of markers / S/F lines too — a physical barrier blocks the
// painted line on the ground).
export const SURFACE_PRIORITY = {
  grass:  0,
  gravel: 1,
  sand:   2,
  tarmac: 3,
  curb:   4,
};

export function comparePatchPriority(a, b) {
  const pa = SURFACE_PRIORITY[a.surface] ?? 99;
  const pb = SURFACE_PRIORITY[b.surface] ?? 99;
  return pa - pb;
}

// Three rendering layers relative to the racing strip:
//
//   'below'  — grass / gravel / sand. Drawn UNDER the strip, pit
//              lane, and pit-lane merge ribbons. Off-track surfaces;
//              ground / runoff.
//   'merge'  — tarmac. Drawn before curbs and the strip, so curbs
//              and the racing surface's white edge band both paint
//              over tarmac patches in the overlap. Past the strip's
//              edge a tarmac runoff patch is still visible because
//              it's drawn after `below` patches.
//   'above'  — curb. Drawn AFTER tarmac patches but BEFORE the
//              strip + pit lane. Curbs cover tarmac patches but the
//              main racing surface still paints over curbs where
//              they overlap.
//
// `surfaceAt` walks layers in visual top-down order — strip → above →
// pit lane → merge → below → default — so the simulator reports the
// same surface that's visually on top at any world point.
export function patchLayer(patch) {
  const p = SURFACE_PRIORITY[patch.surface] ?? 99;
  if (p >  SURFACE_PRIORITY.tarmac) return 'above';
  if (p === SURFACE_PRIORITY.tarmac) return 'merge';
  return 'below';
}
